"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import Image from "next/image";
import { FaHome, FaChalkboardTeacher, FaChevronDown, FaSignOutAlt, FaUser, FaComments, FaBars, FaTimes } from "react-icons/fa";
import { useRef, useEffect, useState } from "react";
import AITutorThemeToggle from "@/app/ai-tutor/components/AITutorThemeToggle";

const AITutorNavbar = ({ user, onLogout, onToggleSidebar, sidebarOpen }) => {
  const profileImageLoader = ({ src }) => src;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentSearchParams = searchParams.toString();
  const fullCurrentPath = currentSearchParams ? `${pathname}?${currentSearchParams}` : pathname;
  const encodedReturnUrl = encodeURIComponent(fullCurrentPath);

  const isProfilePage = pathname === '/profile';
  const isChatPage = pathname === '/ai-tutor' || pathname.startsWith('/ai-tutor/chat/');

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white px-3 sm:px-4 lg:px-6 shadow-lg transition-all duration-300 py-2.5 sm:py-3 lg:py-4">
      <div className="relative flex items-center justify-between">
        {/* Left - Home & Sidebar Toggle */}
        <div className="flex items-center gap-2 flex-shrink-0 z-10">
          {isChatPage && onToggleSidebar && (
            <button
              onClick={onToggleSidebar}
              className="lg:hidden flex items-center justify-center bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all duration-200 rounded-full text-white w-9 h-9"
              aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
            >
              {sidebarOpen ? <FaTimes className="text-sm" /> : <FaBars className="text-sm" />}
            </button>
          )}
          <Link
            href="/"
            className="flex items-center space-x-1.5 sm:space-x-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all duration-200 rounded-full text-white px-3 sm:px-3 lg:px-4 py-1.5 sm:py-1.5 lg:py-2"
            title="Go to Home"
          >
            <FaHome className="transition-all duration-300 text-sm" />
            <span className="font-medium transition-all duration-300 hidden xs:inline text-sm">Return</span>
          </Link>
        </div>

        {/* Center - Title (Absolutely centered) */}
        <Link href="/ai-tutor" className="absolute left-1/2 transform -translate-x-1/2 flex items-center space-x-2 sm:space-x-2 lg:space-x-4">
          <div className="bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 w-9 h-9 sm:w-10 sm:h-10 lg:w-12 lg:h-12">
            <FaChalkboardTeacher className="text-white transition-all duration-300 text-base sm:text-lg lg:text-xl" />
          </div>
          <div className="text-left">
            <h1 className="font-bold transition-all duration-300 whitespace-nowrap text-lg sm:text-xl lg:text-2xl">
              <span className="hidden sm:inline">JEE Challenger AI Tutor</span>
              <span className="inline sm:hidden">AI Tutor</span>
            </h1>
            <p className="text-blue-100 transition-all duration-300 whitespace-nowrap hidden md:block text-sm">
              Your personalized JEE preparation assistant
            </p>
          </div>
        </Link>

        {/* Right - Theme toggle, user menu */}
        <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-3 flex-shrink-0 z-10">
          <AITutorThemeToggle />
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen((open) => !open)}
              className="flex items-center sm:space-x-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all duration-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-400 px-1.5 sm:px-3 py-1.5 sm:py-1.5 lg:py-2"
              title="User menu"
              aria-label="User menu"
            >
              {user.image ? (
                <Image
                  src={user.image}
                  alt={user.name}
                  width={36}
                  height={36}
                  loader={profileImageLoader}
                  unoptimized
                  className="rounded-full border-2 border-white/30 object-cover transition-all duration-300 flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 lg:w-9 lg:h-9"
                />
              ) : (
                <div className="rounded-full border-2 border-white/30 bg-white/20 flex items-center justify-center transition-all duration-300 flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 lg:w-9 lg:h-9">
                  <FaUser className="text-white transition-all duration-300 text-sm" />
                </div>
              )}
              <span className="font-medium text-white hidden md:inline transition-all duration-300 truncate max-w-[100px] lg:max-w-[150px] text-sm">{user.name}</span>
              <FaChevronDown className={`text-white transition-all duration-300 hidden sm:inline text-sm ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-44 sm:w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl sm:rounded-2xl shadow-2xl z-50 overflow-hidden backdrop-blur-sm">
                {/* Menu Items */}
                <div className="py-1 sm:py-2">
                  {isProfilePage ? (
                    <Link
                      href="/ai-tutor"
                      onClick={() => setDropdownOpen(false)}
                      className="w-full flex items-center px-3 sm:px-4 py-2 sm:py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-150 space-x-2 sm:space-x-3 group"
                    >
                      <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center flex-shrink-0">
                        <FaComments className="text-blue-500 group-hover:text-blue-600 text-sm" />
                      </div>
                      <span className="font-medium">Chat</span>
                    </Link>
                  ) : (
                    <Link
                      href={`/profile?returnUrl=${encodedReturnUrl}`}
                      onClick={() => setDropdownOpen(false)}
                      className="w-full flex items-center px-3 sm:px-4 py-2 sm:py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-150 space-x-2 sm:space-x-3 group"
                    >
                      <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center flex-shrink-0">
                        <FaUser className="text-blue-500 group-hover:text-blue-600 text-sm" />
                      </div>
                      <span className="font-medium">Profile</span>
                    </Link>
                  )}
                  <div className="border-t border-gray-100 dark:border-gray-700 my-0.5 sm:my-1"></div>
                  <button
                    onClick={() => { setDropdownOpen(false); onLogout(); }}
                    className="w-full flex items-center px-3 sm:px-4 py-2 sm:py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150 space-x-2 sm:space-x-3 group"
                  >
                    <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center flex-shrink-0">
                      <FaSignOutAlt className="text-red-500 group-hover:text-red-600 text-sm" />
                    </div>
                    <span className="font-medium">Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITutorNavbar;